import { Salon, Stylist } from 'src/app/models';

import { ENV_CONFIG } from 'src/bin/env.config';
import { ErrorHandlerService } from '../services/error-handler.service';
import { Auth } from '../services/login.service';
import { InAppPurchaseService } from '../services/inapp-purchase.service';
import { PostHogService } from './posthog.service';
import { IntercomService } from './intercom.service';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OneSignal } from '@awesome-cordova-plugins/onesignal/ngx';
import { StylistService } from '../services/stylist.service';
import { Utilities } from '../utilities/utilities';
import { switchMap } from 'rxjs/operators';
import { Platform, ToastController } from '@ionic/angular';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { presentLiveUpdateToast } from '../utilities/live-update-toast';
import { readParams } from '../utilities/query-string.util';
import * as Sentry from '@sentry/angular';
import { SentryContextService } from './sentry-context.service';
import { HIDE_OFF_STAFF_KEY_PREFIX } from '../utilities/all-staff-storage-keys';

/** Where an ended admin impersonation returns to. */
const ADMIN_AREA_URL = 'https://salonmonster.com/internal/stats.php';

/** Fields the API adds to a signed token for the admin "log in as" flow. */
interface ImpersonationToken {
  impersonation: boolean;
  impersonatorName?: string;
  sessionExpiry?: number;
  targetUserID?: number;
  targetUserType?: string;
  userID?: number;
  userType?: string;
  salonID?: number;
  administrator?: number;
}

declare var $crisp: any;
declare var window: any;

/**
 * localStorage keys that survive clearSession(). Anything NOT matched here is
 * session state this app owns and is cleared on logout / on boot without auth.
 *
 * `ph_*` is PostHog's persisted state (`ph_<projectKey>_posthog`) — not ours to
 * delete. Identity is dropped explicitly via postHogService.reset() in logout() instead.
 *
 * The All Staff hide-staff choice is keyed to one staff member, so it can
 * outlive their log-out on a shared device.
 */
const PRESERVED_ON_CLEAR: RegExp[] = [
  /^firstVisit$/,
  /^ph_/,
  new RegExp(`^${HIDE_OFF_STAFF_KEY_PREFIX}\\d+$`),
];

// export interface User {
//   user: any;
//   salon: any;
//   token: string;
// }

// declare global {
//     interface Window { $crisp: any; }
// }

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private loggedIn: boolean;

  private user: Stylist;

  private salon: Salon;

  private token: string;

  private logInMonitor = new BehaviorSubject<boolean>(false);

  private logInMonitor$ = this.logInMonitor.asObservable();

  private logOutMonitor = new BehaviorSubject<boolean>(false);

  private logOutMonitor$ = this.logOutMonitor.asObservable();
  public sessionMonitor = new BehaviorSubject<Auth | undefined>(undefined);
  private sessionMonitor$ = this.sessionMonitor.asObservable();

  private isSideBarShowing = true;

  private profileImageMonitor = new BehaviorSubject<any>(null);
  public profileImageMonitor$ = this.profileImageMonitor.asObservable();

  private impersonationTokenCache: { token: string; parsed: ImpersonationToken | null } | undefined;

  constructor(
    private platform: Platform,
    // private stylistService: StylistService,
    // private salonService: SalonService,
    private oneSignal: OneSignal,
    protected errorHandlerService: ErrorHandlerService,
    private inAppPurchaseService: InAppPurchaseService,
    private postHogService: PostHogService,
    private intercomService: IntercomService,
    private toastCtrl: ToastController,
    private sentryContext: SentryContextService
  ) {
    this.platform.ready().then(() => {
      // window.$crisp=[];

      if (this.platform.is('capacitor')) {
        //   // START IN APP PURCHASE
        // this.inAppPurchaseService.init();

        //   // START One SIGNAL
        // this.oneSignal.startInit(
        //   ENV_CONFIG.oneSignalAppID,
        //   ENV_CONFIG.fireBaseSenderID
        // );

        // this.oneSignal.inFocusDisplaying(
        //   this.oneSignal.OSInFocusDisplayOption.Notification
        // );

        // this.oneSignal.handleNotificationReceived().subscribe(() => {
        //   // do something when notification is received
        // });

        // this.oneSignal.handleNotificationOpened().subscribe(() => {
        //   // do something when a notification is opened
        // });

        // this.oneSignal.endInit();
        const addNotificationReceivedListener = async () => {
          await FirebaseMessaging.addListener(
            'notificationReceived',
            (event) => {}
          );
        };

        const addNotificationActionPerformedListener = async () => {
          await FirebaseMessaging.addListener(
            'notificationActionPerformed',
            (event) => {}
          );
        };
      }
    });

    if (Utilities.isHTM5LocalStorageSupported()) {
      try {
        const auth: Auth = JSON.parse(localStorage.getItem('auth'));
        if (auth) {
          auth.user = JSON.parse(localStorage.getItem('user'));
          auth.salon = JSON.parse(localStorage.getItem('salon'));
          // A stored session missing any part would restore half logged in,
          // so clear it and let the user sign in again. Mirrors
          // hasCompleteSession(), on the raw stored JSON.
          if (auth.token && auth.user?.id && auth.salon?.id) {
            this.login(auth);
          } else {
            this.clearSession();
          }
        } else {
          this.clearSession();
        }
      } catch (err) {
        this.clearSession();
      }
    } else {
      this.clearSession();
    }

    // refresh user's session immediately
    // this.refreshSession().subscribe();

    // refresh user and salon object every 15 mins
    // const fifteenMins = 1000 * 60 * 15;
    // setInterval(() => {
    //   this.refreshSession().subscribe();
    // }, fifteenMins);
  }

  // public refreshSession () : Observable<boolean> {

  //   return new Observable <boolean> (observer => {
  //     observer.next(true);
  //     observer.complete();
  //   });

  // return new Observable <boolean> (observer => {
  //   if (this.isLoggedIn()) {
  //     this.stylistService.loadStylistInfo(this.user.id)
  //       .pipe(
  //         switchMap((stylist: Stylist) => {
  //           this.setUser(stylist);
  //           return this.salonService.load();
  //         })
  //       )
  //       .subscribe((salon: Salon) => {
  //         this.setSalon(salon);

  //         this.sessionMonitor.next({
  //           user: this.user,
  //           salon: this.salon,
  //           token: undefined
  //         });

  //         observer.next(true);
  //         observer.complete();
  //       }, (err) => {
  //         observer.error(this.errorHandlerService.handleError(err));
  //         observer.complete();
  //       });
  //   } else {
  //     // observer.error(new Error('User is not logged in'));
  //     // observer.complete();
  //   }
  // });
  // }

  public login(data: Auth) {
    this.clear();
    const apiToken = data.token;
    const userData = data.user;
    const salonData = data.salon;
    const tokenObj = JSON.parse(apiToken);
    const userID = tokenObj.user_id ?? tokenObj.userID;

    if (Utilities.isHTM5LocalStorageSupported()) {
      try {
        // intercomUserJwt is runtime-only credential material; strip it
        // before persisting — the Stylist.toJSON() exclusion does not cover
        // this raw API payload (JSON.stringify drops undefined keys).
        const persistableAuth = data && data.user
          ? { ...data, user: { ...data.user, intercomUserJwt: undefined } }
          : data;
        localStorage.setItem('auth', JSON.stringify(persistableAuth));
        localStorage.setItem('token', apiToken);
        localStorage.setItem('userID', userID);
        localStorage.setItem('stylistID', userID);
        localStorage.setItem('salonID', tokenObj.salonID);
        localStorage.setItem('administrator', tokenObj.administrator);
      } catch (err) {}
    }
    const user: Stylist = Stylist.parseStylistData(userData);
    const salon: Salon = Salon.parse(salonData);
    this.setUser(user);
    this.setSalon(salon);
    this.token = apiToken;
    this.loggedIn = true;
    this.logInMonitor.next(true);

    const stylistFullName = user.getFirstName() + ' ' + user.getLastName();
    const stylistEmail = user.getEmail();
    const appVersion = salon.getAppVersion();
    const appBuildNum = ENV_CONFIG.appVersion;
    const salonName = salon.getName();
    const hasCalendar = user.getCalendar() === 1 ? 'Yes' : 'No';
    const isAdmin = user.isAdministrator() ? 'Yes' : 'No';
    const canViewAllCalendars = user.getViewAllCalendars() === 1 ? 'Yes' : 'No';
    const canViewAllClients = user.getViewAllClients() === 1 ? 'Yes' : 'No';
    const hasSettingsControl = user.getSettingsControl() === 1 ? 'Yes' : 'No';
    const requiresApptConfirmation =
      user.getConfirmApptsManually() === 1 ? 'Yes' : 'No';
    const timeZone = salon.getTimeZone();
    const country = this.salon.getCountry()
      ? this.salon.getCountry().getCountryName()
      : '';
    const readOnly = this.salon.isReadOnly() ? 'Yes' : 'No';
    const salonWebsite = this.salon.getWebsite();
    const hasStripeAccount = this.salon.hasStripeAccount() ? 'Yes' : 'No';
    const stylistID = user.getID();

    // Identify the session to Sentry by the salon and stylist IDs plus the
    // stylist's own email, so support can find the session of whoever reported
    // a problem. Client data is never sent (clients are tagged by ID only).
    // During an impersonation this sets no Sentry user, no stylistID and no
    // email, only the salon and `impersonating: true`, so support's clicks are
    // never attributed to the real stylist.
    this.sentryContext.identify({
      salonID: salon.getId(),
      stylistID,
      stylistEmail,
      impersonating: this.isImpersonating(),
    });

    // Skip analytics and help-chat identification entirely during an admin
    // impersonation session: the session runs on the target stylist's ID but
    // with placeholder profile data, so identifying would write fabricated
    // name/email onto the real stylist's PostHog record, and neither the admin
    // nor the stylist should be booted into Intercom under the
    // other's identity.
    if (this.isImpersonating()) {
      // Flag-evaluation properties are the one exception. Release conditions
      // target salon_id, so without this an impersonation session resolves
      // every salon-targeted flag to false and shows the admin a different
      // app than the salon actually sees — which defeats the point of
      // logging in as them. These properties are sent only with the /flags
      // request: they are NOT written to a person profile, no identify()
      // happens, the distinct id stays the anonymous device id, and nothing
      // is attributed to the real stylist. logout() -> postHogService.reset()
      // clears them when impersonation ends.
      this.postHogService.setPersonPropertiesForFlags({
        salon_id: String(salon.getId()),
      });
      return;
    }

    // Link user to PostHog — merges the anonymous distinct_id (from the
    // marketing site cookie, if any) with the authenticated user so the
    // full funnel from ad click to signup lives on one person record.
    // Called on every UserService.login() invocation including session
    // restore on boot; identifying an already-identified user is a safe
    // no-op per PostHog docs, so no need to guard against repeats.
    this.postHogService.identify(String(stylistID), {
      name: stylistFullName,
      email: stylistEmail,
      // Person-level salon id so feature flags can target whole salons
      // (release conditions match on person properties; every stylist in the
      // salon carries the same value).
      salon_id: String(salon.getId()),
      company_name: salonName,
      app_version: appVersion,
      app_build_num: appBuildNum,
      is_admin: user.isAdministrator(),
      has_calendar: user.getCalendar() === 1,
      can_view_all_calendars: user.getViewAllCalendars() === 1,
      can_view_all_clients: user.getViewAllClients() === 1,
      has_settings_control: user.getSettingsControl() === 1,
      requires_appt_confirmation: user.getConfirmApptsManually() === 1,
      time_zone: timeZone,
      country: country,
      read_only: this.salon.isReadOnly(),
      salon_website: salonWebsite,
      has_stripe_account: this.salon.hasStripeAccount(),
    });

    this.intercomService.startSession(user, salon, this.token);
  }

  /** Alias of isImpersonating(): true when support staff are impersonating a stylist. */
  public isImpersonationSession(): boolean {
    return this.isImpersonating();
  }

  public isLoggedIn(): boolean {
    return this.loggedIn;
  }

  /**
   * True only when the session has everything a page needs: a token, and a
   * stylist and salon that each carry an id. `loggedIn` alone is not enough —
   * a session can be flagged logged in while its user or salon never loaded,
   * which renders the menu over a calendar that cannot load.
   */
  public hasCompleteSession(): boolean {
    return !!(
      this.loggedIn &&
      this.token &&
      this.user?.getID() &&
      this.salon?.getId()
    );
  }

  public getLogInMonitor(): Observable<boolean> {
    return this.logInMonitor$;
  }

  public getLogOutMonitor(): Observable<boolean> {
    return this.logOutMonitor$;
  }

  public getSessionMonitor(): Observable<Auth | undefined> {
    return this.sessionMonitor$;
  }

  public logout() {
    if (this.platform.is('capacitor') && this.isLoggedIn()) {
      this.oneSignal.deleteTag('stylistID');
    }

    this.clearSession();

    // Drop the PostHog identity and regenerate an anonymous distinct_id so
    // the next visitor on this device doesn't get attributed to the user
    // who just logged out. Must come after clearSession so there's no
    // race where identify() re-runs against a half-cleared auth state.
    this.postHogService.reset();

    // broadcast log out status
    this.logOutMonitor.next(true);
  }

  public clearSession() {
    this.sentryContext.clearIdentity();
    if (Utilities.isHTM5LocalStorageSupported()) {
      // Wipe the session, but only the keys this app owns. A blanket
      // localStorage.clear() also deleted PostHog's persisted state
      // (ph_<projectKey>_posthog), and the constructor calls clearSession()
      // at boot whenever there is no stored auth — the path a visitor from
      // the marketing site hits at #/register.
      //
      // posthog-js keeps that state in memory and rewrites the whole key on
      // its next save (any capture does one), so the blanket clear() only
      // lost it when the page closed before that save. Preserving the key
      // closes that window and stops us deleting storage we do not own.
      //
      // Logout still drops identity: logout() calls postHogService.reset()
      // right after this.
      const preserved = Object.entries(localStorage)
        .filter(([key]) => PRESERVED_ON_CLEAR.some((p) => p.test(key)));
      localStorage.clear();
      preserved.forEach(([key, value]) => localStorage.setItem(key, value));
    }
    this.clear();
  }

  public getUser(): Stylist {
    return this.user;
  }

  public setUser(user: Stylist) {
    // The API only gives the Intercom hash to its owner. A copy of the same
    // stylist from a staff list (booking settings, the stylist picker) has none,
    // and saving it here would lose the hash for the next reloaded session.
    const current = this.user;
    if (user && current && !user.intercomUserHash && current.intercomUserHash
        && user.getID() === current.getID()) {
      user = user.setIntercomUserHash(current.intercomUserHash);
    }
    if (user && current && user.getID() === current.getID() && !user.intercomUserJwt && current.intercomUserJwt) {
      user = new Stylist({ ...user.toJSON(), intercomUserJwt: current.intercomUserJwt });
    }
    this.user = user;

    if (Utilities.isHTM5LocalStorageSupported()) {
      localStorage.setItem('user', JSON.stringify(user.toStoredSession()));
    }
  }

  public getSalon(): Salon {
    return this.salon;
  }

  public setSalon(salon: Salon) {
    this.salon = salon;
    if (Utilities.isHTM5LocalStorageSupported()) {
      localStorage.setItem('salon', JSON.stringify(this.salon.toStoredSession()));
    }
    void this.syncLiveUpdateChannel();
  }

  /**
   * The shared release key = trailing 10 digits of the native version code
   * (`YYYYMMDD` + 2-digit daily release). Identical on iOS and Android for the
   * same release even though iOS's CFBundleVersion carries a 2-digit version
   * prefix (e.g. iOS 112026051101 & Android 2026051101 both -> "2026051101").
   * Cached after the first read. `undefined` if the version code is malformed.
   */
  private releaseKey?: string;

  private async getReleaseKey(): Promise<string | undefined> {
    if (this.releaseKey) return this.releaseKey;
    try {
      const { versionCode } = await LiveUpdate.getVersionCode();
      if (versionCode && /^\d{10,}$/.test(versionCode)) {
        this.releaseKey = versionCode.slice(-10);
        return this.releaseKey;
      }
      console.warn(
        '[OTA] Unexpected versionCode shape; using an unversioned channel:',
        versionCode
      );
    } catch (err) {
      console.warn('[OTA] getVersionCode failed; using an unversioned channel', err);
    }
    return undefined;
  }

  /**
   * Align the Live Update plugin's channel with the salon's release track.
   * The channel is `<track>-<releaseKey>` (Versioned Channels) so a device only
   * pulls bundles built for its own native binary; it falls back to a plain
   * `<track>` channel if the release key can't be read. On change, fetch the
   * latest bundle so the new track takes effect on the next app start rather
   * than waiting for a later resume.
   *
   * Deliberately does NOT prompt. This runs from setSalon(), which fires on
   * login, on every 15-minute session refresh, and on ten different settings
   * saves — a native confirm() on any of those freezes the WebView (every JS
   * timer stops and then fires at once on dismissal, which can trip the
   * calendar's own load watchdog into a failure that never happened), and
   * answering it called reload() immediately, discarding whatever was on
   * screen — a half-built booking or an open checkout at the 15-minute tick.
   * Capawesome applies the staged bundle on the next app start via ready(),
   * so nothing is lost by staying quiet; AppComponent.checkForLiveUpdate()
   * owns surfacing the non-blocking "Restart now" offer.
   */
  private async syncLiveUpdateChannel(): Promise<void> {
    if (!this.platform.is('capacitor')) return;
    const track = this.salon?.getReleaseTrack() || 'production';
    const releaseKey = await this.getReleaseKey();
    const desired = releaseKey ? `${track}-${releaseKey}` : track;
    try {
      const stylistKey = this.user?.getStylistKey();
      if (stylistKey) {
        await LiveUpdate.setCustomId({ customId: stylistKey });
      }
      const { channel: current } = await LiveUpdate.getChannel();
      if (current === desired) return;
      await LiveUpdate.setChannel({ channel: desired });
      const { nextBundleId } = await LiveUpdate.sync();
      if (nextBundleId) {
        await presentLiveUpdateToast(this.toastCtrl);
      }
    } catch (err) {
      // Non-fatal — the channel retries on the next salon load. But it must not
      // be silent: this is the other half of OTA delivery, and it runs on
      // login, on the 15-minute session refresh and on every settings save, so
      // a persistent failure here strands a device on its current bundle with
      // nothing to show for it.
      console.error('[OTA] channel sync failed', err);
      this.postHogService.capture('ota_update_check_failed', {
        source: 'channel_sync',
      });
      Sentry.addBreadcrumb({
        category: 'ota.sync',
        message: 'channel sync failed',
        level: 'warning',
      });
    }
  }


  public getToken(): string {
    return this.token;
  }

  // ---------------------------------------------------------------------------
  // Admin impersonation ("log in as").
  //
  // The PHP internal tool exchanges the admin action for a signed impersonation
  // token server-side (POST /auth/impersonate needs the server API key, so the
  // app never calls it) and redirects into the app with that token in the URL.
  // The token carries `impersonation: true`, `impersonatorName`,
  // `sessionExpiry` (ms epoch, also enforced by the API on every request) and
  // `targetUserID` / `targetUserType` alongside the normal signed fields.
  // ---------------------------------------------------------------------------

  /** Parses a token string; null unless it is a well-formed impersonation token. */
  private static parseImpersonationToken(token: string): ImpersonationToken | null {
    try {
      const tokenObj = JSON.parse(token);
      return tokenObj && tokenObj.impersonation === true ? tokenObj : null;
    } catch {
      return null;
    }
  }

  /**
   * Parsed token when the current session is an impersonation, else null.
   * Memoized on the token string: AuthGuard asks on every navigation and the
   * banner polls, so the same token must not be re-parsed each time.
   */
  private getImpersonationToken(): ImpersonationToken | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }
    if (this.impersonationTokenCache?.token !== token) {
      this.impersonationTokenCache = { token, parsed: UserService.parseImpersonationToken(token) };
    }
    return this.impersonationTokenCache.parsed;
  }

  public isImpersonating(): boolean {
    return this.getImpersonationToken() !== null;
  }

  public getTargetUser(): { id: number; type: string; name: string } | null {
    const tokenObj = this.getImpersonationToken();
    if (!tokenObj) {
      return null;
    }
    const user = this.getUser();
    return {
      id: tokenObj.targetUserID ?? tokenObj.userID,
      type: tokenObj.targetUserType || 'User',
      name: user ? user.getFirstName() + ' ' + user.getLastName() : 'Unknown User'
    };
  }

  public getImpersonator(): { name: string } | null {
    const tokenObj = this.getImpersonationToken();
    if (!tokenObj) {
      return null;
    }
    return { name: tokenObj.impersonatorName || 'Admin User' };
  }

  /** Millisecond epoch at which the impersonation session expires, or null. */
  public getSessionExpiry(): number | null {
    const tokenObj = this.getImpersonationToken();
    return tokenObj && typeof tokenObj.sessionExpiry === 'number' ? tokenObj.sessionExpiry : null;
  }

  public isSessionExpired(): boolean {
    const expiry = this.getSessionExpiry();
    return expiry !== null && Date.now() > expiry;
  }

  /**
   * Consume an impersonation token from the landing URL: `?token=` before the
   * `#` or `#/?token=` after it (readParams merges both, see
   * docs/solutions/architecture-patterns/read-query-params-under-hashlocationstrategy.md).
   * The token is scrubbed from the URL before anything else can observe it.
   * Returns true when a new impersonated session was established.
   */
  public consumeImpersonationTokenFromUrl(
    search: string = window.location.search,
    hash: string = window.location.hash
  ): boolean {
    const token = readParams(search, hash, ['token']).token;
    if (!token) {
      return false;
    }
    window.history.replaceState({}, document.title, window.location.pathname);
    return this.loginImpersonationFromToken(token);
  }

  /**
   * Establish an impersonated session from a signed impersonation token.
   * Anything that is not a live impersonation token is ignored without
   * touching the existing session: this is the only URL-driven login path,
   * so a crafted link must never be able to replace a user's session.
   */
  public loginImpersonationFromToken(token: string): boolean {
    const tokenObj = UserService.parseImpersonationToken(token);
    if (!tokenObj) {
      console.warn('Ignoring URL token: not an impersonation token');
      return false;
    }
    if (typeof tokenObj.sessionExpiry !== 'number' || Date.now() > tokenObj.sessionExpiry) {
      console.warn('Ignoring URL token: impersonation session already expired');
      return false;
    }

    // Replace whatever session was there.
    this.clearSession();

    // Only the token arrives in the URL. Log in with the identity fields it
    // carries; the logInMonitor emission makes BackgroundProcessService
    // refreshSession() load the real stylist and salon (and emit
    // sessionMonitor). login() skips PostHog / chat identify for impersonation
    // sessions and gives Sentry only the salon ID, so these placeholders never
    // reach third parties.
    const userID = tokenObj.targetUserID ?? tokenObj.userID;
    const authData: Auth = {
      token,
      user: {
        id: userID,
        firstName: 'Loading',
        lastName: 'stylist',
        userType: tokenObj.targetUserType || tokenObj.userType || 'stylist',
        administrator: tokenObj.administrator || 0,
        active: 1
      },
      salon: {
        id: tokenObj.salonID,
        salonName: 'Loading salon',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    };
    this.login(authData);
    return true;
  }

  /** End the impersonation: full logout, then back to the internal admin area. */
  public endImpersonation(): void {
    this.logout();
    this.navigateTo(ADMIN_AREA_URL);
  }

  /** Seam for tests: assigning window.location.href navigates the test page away. */
  protected navigateTo(url: string): void {
    window.location.href = url;
  }

  private clear() {
    this.loggedIn = false;
    this.user = undefined;
    this.salon = undefined;
    this.token = undefined;
    // this.stylistService.setOutdated();

    // Tear down Intercom so the next user on this device cannot see this
    // user's conversations.
    if (!this.intercomService.shutdown()) {
      console.error('[UserService] Intercom shutdown failed during logout cleanup');
    }
  }

  public getSideBarVisibility(): boolean {
    return this.isSideBarShowing;
  }

  public setSideBarVisiblity(visible: boolean) {
    this.isSideBarShowing = visible;
  }

  updateProfileImg(newData: any) {
    this.profileImageMonitor.next(newData);
  }
}
